import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import styles from './stats.module.scss';
import { useGetStatsInscriptions } from '~/hooks/stats';
import type { StatsInscriptions } from '~/types/stats';
import ThreeSegmentYearTile from '~/components/ThreeSegmentYearTile';
import GlobalDistributionTile from '~/components/GlobalDistributionTile';
import Loader from '~/components/Loader';
import CustomAreaChart from '~/components/AreaChart';
import HorizontalBarsTile from '~/components/HorizontalBarsTile';

type EvolutionPoint = StatsInscriptions['evolutionDesInscriptions']['points'][number];

const toCumulativeEvolutionPoints = (points: EvolutionPoint[]): EvolutionPoint[] => {
  let cum2026 = 0;
  let cum2025 = 0;

  return points.map((point) => {
    cum2026 += point.y2026 ?? 0;
    cum2025 += point.y2025 ?? 0;
    let evolutionPct = 0;
    if (cum2025 === 0) {
      evolutionPct = cum2026 > 0 ? 100 : 0;
    } else {
      evolutionPct = Math.round(((cum2026 - cum2025) / cum2025) * 100);
    }

    return {
      ...point,
      y2026: cum2026,
      y2025: cum2025,
      evolutionPct,
    };
  });
};

const StatistiquesInscriptions = () => {
  const [isCumulative, setIsCumulative] = useState(true);
  const [searchParams] = useSearchParams();
  const region = searchParams.get('region') || undefined;

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useGetStatsInscriptions({ region });

  const currentYear = data?.nombreMagasinsInscrits?.currentYear;
  const previousYear = data?.nombreMagasinsInscrits?.previousYear;
  const lunchCurrentYear = data?.totalInscriptionsAuxRepas?.currentYear;
  const lunchPreviousYear = data?.totalInscriptionsAuxRepas?.previousYear;
  const repartitionGlobale = data?.repartitionGlobale;
  const repartitionParEnseigne = data?.repartitionParEnseigne || [];
  const evolutionPoints = data?.evolutionDesInscriptions?.points || [];
  const chartEvolutionPoints = useMemo(
    () => (isCumulative ? toCumulativeEvolutionPoints(evolutionPoints) : evolutionPoints),
    [evolutionPoints, isCumulative],
  );
  const repartitionParRegion = data?.repartitionDuNombreDeMagasins || [];

  if (isLoading || isFetching) {
    return (
      <div className={styles.pageLoader}>
        <Loader color="#007D92" />
      </div>
    );
  }

  if (error) {
    return <p>Erreur de chargement.</p>;
  }

  return (
    <>
      <div className={styles.tilesGrid}>
        <ThreeSegmentYearTile
          title="Nombre de magasins inscrits"
          rows={{
            primary: currentYear || {
              year: 2026,
              j1Only: 0,
              j2Only: 0,
              j1j2: 0,
            },
            secondary: previousYear || {
              year: 2025,
              j1Only: 0,
              j2Only: 0,
              j1j2: 0,
            },
          }}
        />

        <ThreeSegmentYearTile
          title="Total inscriptions aux repas"
          mergeBothDaysIntoJ1AndJ2
          rows={{
            primary: lunchCurrentYear || {
              year: 2026,
              j1Only: 0,
              j2Only: 0,
              j1j2: 0,
            },
            secondary: lunchPreviousYear || {
              year: 2025,
              j1Only: 0,
              j2Only: 0,
              j1j2: 0,
            },
          }}
        />

        <div className={styles.tileWide}>
          <GlobalDistributionTile
            title="Répartition globale"
            rows={repartitionGlobale || {
              j1Only: { fournisseurs: 0, magasins: 0, collaborateurs: 0 },
              j2Only: { fournisseurs: 0, magasins: 0, collaborateurs: 0 },
              j1j2: { fournisseurs: 0, magasins: 0, collaborateurs: 0 },
              total: { fournisseurs: 0, magasins: 0, collaborateurs: 0 },
              visiteursUniquesTotal: 0,
            }}
          />
        </div>

        <HorizontalBarsTile
          title="Répartition par enseigne"
          items={repartitionParEnseigne}
        />

        <section className={styles.tile}>
          <div className={styles.tileChartHeader}>
            <h2>Evolution des inscriptions (toute typologie)</h2>
            <label className={styles.cumulativeToggle}>
              <input
                type="checkbox"
                checked={isCumulative}
                onChange={(e) => setIsCumulative(e.target.checked)}
              />
              Cumuler les données
            </label>
          </div>
          <div className={styles.tileBody}>
            <div className={styles.chartContainer}>
              <CustomAreaChart
                data={chartEvolutionPoints}
                tooltipMode="weeklyComparison"
                keys={[
                  {
                    name: '2026',
                    dataKey: 'y2026',
                    color: '#007D8F',
                    id: 'inscriptions-2026',
                    withFill: true,
                  },
                  {
                    name: '2025',
                    dataKey: 'y2025',
                    color: '#007D8F',
                    id: 'inscriptions-2025',
                    withFill: false,
                    strokeDasharray: '4 4',
                  },
                ]}
              />
            </div>
          </div>
        </section>

        <HorizontalBarsTile
          title="Répartition du nombre de magasins"
          items={repartitionParRegion}
        />
      </div>

      {/* `data` est prêt pour la phase UI */}
      {data && null}
    </>
  );
};

export default StatistiquesInscriptions;
